import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { CellContext } from '@tanstack/react-table';

import { WebEDIRowData } from 'support/types/webediTable';
import { shouldShowCollapseToggle } from 'components/DataTable/helpers/shouldShowCollapseToggle';
import { getItemsCountAtPath } from 'components/DataTable/hooks/useEmbeddedTableRows/helpers';
import { Button } from 'components/Form/Button';

import { generateEmbeddedActionTestId } from '../../../utils/generateEmbeddedActionTestId';

type Props = CellContext<WebEDIRowData, unknown>;

/**
 * Chevron that hides or shows one embedded table — this row's items for one column group.
 *
 * It sits inside the group, so `calculateCellRowSpan` gives it the whole row's height and renders
 * it once rather than once per array item. It keeps its own cell beside the collapsed summary,
 * which is what makes a collapsed group reopenable.
 */
export const CollapseEmbeddedRowsCell = ({ row, column, table }: Props) => {
  const { t } = useTranslation();
  const tableMeta = table.options.meta;
  const parentColumnPath = column.parent?.id;
  const onToggleGroupCollapsed = tableMeta?.onToggleGroupCollapsed;
  const isCollapsed = Boolean(parentColumnPath && tableMeta?.collapsedGroups?.has(`${row.index}::${parentColumnPath}`));

  const testId = useMemo(
    () =>
      generateEmbeddedActionTestId({
        dataPrefix: tableMeta?.dataPrefix,
        rowIndex: row.index,
        parentColumnPath,
        suffix: 'collapseEmbeddedRowsCell',
      }),
    [tableMeta?.dataPrefix, row.index, parentColumnPath],
  );

  const handleToggle = useCallback(() => {
    if (parentColumnPath) {
      onToggleGroupCollapsed?.(row.index, parentColumnPath);
    }
  }, [onToggleGroupCollapsed, row.index, parentColumnPath]);

  const itemsCount = parentColumnPath ? getItemsCountAtPath(row.original, parentColumnPath) : 0;

  if (!onToggleGroupCollapsed || !parentColumnPath || !shouldShowCollapseToggle({ itemsCount, isCollapsed })) {
    return null;
  }

  // No children: `iconOnly` with children renders a Button tooltip, which overlaps the adjacent
  // row in this narrow column. The chevron direction plus aria-label carry the state.
  const label = isCollapsed ? t('webedi:embeddedRows.expand') : t('webedi:embeddedRows.collapse');

  return (
    <div className="flex size-full items-center justify-center">
      <Button
        iconOnly
        size="extra-small"
        variant="minimal"
        LeftIcon={isCollapsed ? ChevronRightIcon : ChevronDownIcon}
        onClick={handleToggle}
        analyticsId="table_embedded_rows_toggle"
        aria-expanded={!isCollapsed}
        aria-label={label}
        testId={testId}
      />
    </div>
  );
};
